import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  adaptPublicLandingResponse,
  getPublicLanding,
  getSiteDraftPreview,
} from "../api/landing";
import { useProjectConfig } from "./ProjectConfigContext";
import { syncDocumentBranding } from "../utils/documentBranding";
import {
  clearSitePreviewSession,
  resolveSitePreviewSession,
  shouldDiscardSitePreviewSession,
} from "../utils/sitePreviewSession";
import { normalizeImagePosition } from "../utils/segmentVisualStyle";
import { resolveSiteExperienceDefaults } from "../utils/siteExperienceDefaults";
import { createMegaMenuVisualDefaults, materializeMegaMenuVisual } from "../components/navbar/megaMenuVisualOwnership";
import { IMAGE_CAROUSEL_VISUAL_DEFAULTS, TESTIMONIALS_VISUAL_DEFAULTS, materializeCarouselTestimonialsOwnership } from "../utils/sectionVisualOwnership";
import { FLOATING_WHATSAPP_STYLE_DEFAULTS } from "../utils/floatingWhatsappVisualOwnership";
import { HIGHLIGHT_VISUAL_DEFAULTS, materializeHighlightsOwnership, materializeHighlightItemsVisual } from "../utils/highlightVisualOwnership";
import { PRODUCTS_PAGE_VISUAL_DEFAULTS, materializeProductsPageVisualFromLegacy } from "../utils/productsPageVisualOwnership";
import { WEB_PUSH_PROMPT_STYLE_DEFAULTS, materializeWebPushPromptVisualOwnership } from "../utils/webPushPromptVisualOwnership";
import { CONTACT_PAGE_STYLE_DEFAULTS, materializeContactPageVisualOwnership } from "../utils/contactPageVisualOwnership";

const LandingContext = createContext(null);

const DEFAULT_MEGA_MENU_VISUAL = createMegaMenuVisualDefaults({}, {}, { megaMenuStyle: "auto" });

export const DEFAULT_LANDING = Object.freeze({
  schemaVersion: 26,
  publicStatus: "published",
  revision: 1,
  publishedRevision: 1,
  experienceDefaults: { overrides: [] },
  business: {
    name: "Mi negocio",
    shortDescription: "",
    whatsapp: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    country: "Argentina",
    schedule: "",
    mapEmbedUrl: "",
    socials: {
      instagram: "",
      facebook: "",
      tiktok: "",
      linkedin: "",
      youtube: "",
      website: "",
    },
  },
  navigation: {
    style: "style-1",
    homeUrl: "/",
    showAboutLink: true,
    aboutLabel: "Sobre nosotros",
    aboutUrl: "#about",
    showHighlightsLink: true,
    highlightsLabel: "Por qué elegirnos",
    highlightsUrl: "#highlights",
    productsUrl: "/productos",
    contactUrl: "/contact",
    loginUrl: "/login",
    showProductsLink: true,
    showContactLink: true,
    showCart: true,
    showCustomerAccess: true,
    showSocialLinks: true,
    showLogo: true,
    homeLabel: "Inicio",
    productsLabel: "Productos",
    allProductsLabel: "Ver todos",
    megaMenuStyle: "auto",
    megaMenuLayout: "grid",
    megaMenuColumns: "auto",
    megaMenuAnimation: "slide",
    megaMenuContent: "auto",
    megaMenuDisplay: "both",
    megaMenuHierarchy: "category-first",
    megaMenuVisual: DEFAULT_MEGA_MENU_VISUAL,
    showMegaMenuIntro: false,
    showMegaMenuStats: false,
    showMegaMenuLatest: false,
    megaMenuEyebrow: "",
    megaMenuTitle: "",
    megaMenuDescription: "",
    megaMenuCategoriesSubtitle: "",
    megaMenuSortLabel: "",
    megaMenuLatestLabel: "Nuevos",
    contactLabel: "Contacto",
    loginLabel: "Iniciar sesión",
    accountLabel: "Mi cuenta",
    logoutLabel: "Cerrar sesión",
    cartLabel: "Ver carrito",
    cartTooltip: "Carrito",
    socialTitle: "Redes sociales",
    loadingCategoriesText: "Cargando categorías…",
    emptyCategoriesText: "No hay categorías todavía.",
    categoriesErrorText: "No se pudieron cargar las categorías.",
    menuAriaLabel: "Abrir menú",
    closeMenuAriaLabel: "Cerrar menú",
    cartAriaLabel: "Abrir carrito",
    mobile: {
      itemGap: 5.2,
      shellStyle: { backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", boxShadow: "0 8px 30px rgba(20,25,35,.05)" },
      barStyle: { backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
      drawerStyle: { backgroundColor: "#EEF0F5", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
      drawerHeaderStyle: { backgroundColor: "#EEF0F5", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
      openButtonStyle: { color: "#19242F", backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", hoverColor: "#19242F", hoverBackgroundColor: "#EEF0F5", hoverBorderColor: "rgba(26,26,46,.09)", focusColor: "#F35A3A", size: 42 },
      closeButtonStyle: { color: "#19242F", backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", hoverColor: "#19242F", hoverBackgroundColor: "#EEF0F5", hoverBorderColor: "rgba(26,26,46,.09)", focusColor: "#F35A3A", size: 42 },
      catalogPanelStyle: { backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
      catalogItemStyle: { textColor: "#1A1A2E", mutedTextColor: "#5B5D73", hoverBackgroundColor: "#EEF0F5", focusColor: "#F35A3A" },
    },
    buttons: [],
  },
  hero: {
    eyebrow: "",
    title: "Mi negocio",
    subtitle: "",
    primaryCtaLabel: "Consultar",
    primaryCtaUrl: "/contact",
    primaryCtaRequiresProducts: false,
    primaryCtaShowIcon: true,
    primaryCtaIcon: "explore",
    secondaryCtaLabel: "Ver productos",
    secondaryCtaUrl: "/productos",
    secondaryCtaRequiresProducts: true,
    secondaryCtaShowIcon: true,
    secondaryCtaIcon: "storefront",
    imageUrl: "",
    slides: [],
    rotatingPhrases: [],
    autoplay: true,
    intervalMs: 6500,
    animation: "slide",
    overlay: "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))",
    imagePosition: "center",
    contentHorizontalAlign: "left",
    contentVerticalAlign: "center",
  },
  catalog: {
    mode: "auto",
    homeSource: "catalog",
    productsPage: {
      eyebrow: "",
      title: "",
      productsTitle: "Productos",
      servicesTitle: "Servicios",
      hybridTitle: "Productos + servicio",
      subtitle: "",
      loadingText: "Cargando catálogo…",
      resultSingular: "resultado",
      resultPlural: "resultados",
      searchPlaceholder: "Buscar por nombre, servicio, código, categoría o subcategoría",
      showSearch: true,
      showFilters: true,
      showCategoryFilter: true,
      showSubcategoryFilter: true,
      showTypeFilter: true,
      filtersLabel: "Filtros",
      typeFilterLabel: "Tipo",
      categoryFilterLabel: "Categoría",
      subcategoryFilterLabel: "Subcategoría",
      allTypesLabel: "Productos y servicios",
      allCategoriesLabel: "Todas las categorías",
      allSubcategoriesLabel: "",
      resetLabel: "Limpiar filtros",
      emptyTitle: "No encontramos resultados",
      emptyText: "Probá con otra búsqueda o quitá los filtros activos.",
      emptyResetLabel: "Ver todo el catálogo",
      applyFiltersLabel: "Aplicar filtros",
      loadMoreLabel: "Cargar más",
      paginationMode: "all",
      pageSize: 24,
      sortBy: "createdAt",
      sortDir: "desc",
      visual: PRODUCTS_PAGE_VISUAL_DEFAULTS,
    },
    productPage: {
      backLabel: "Volver al catálogo",
      homeLabel: "",
      catalogLabel: "",
      additionalInfoTitle: "Información adicional",
      showBreadcrumbs: true,
      showStock: true,
      showAttributes: true,
      showCapabilities: true,
      showQuickCart: true,
      showWhatsapp: true,
      descriptionCollapsedLines: 6,
      descriptionMoreLabel: "Ver más",
      descriptionLessLabel: "Ver menos",
      layout: "split",
      imageFit: "contain",
      backgroundColor: "",
      surfaceColor: "",
      textColor: "",
      mutedTextColor: "",
    },
    cards: {
      gap: 18,
      radius: 16,
      backgroundColor: "",
      backgroundOpacity: 1,
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      backgroundImageFit: "cover",
      backgroundImageOpacity: 0.28,
      imagePosition: "top",
      imageFit: "cover",
      imageHeight: 210,
      showImage: true,
      showTitle: true,
      showButton: true,
      showType: true,
      showCategory: true,
      showSubcategory: true,
      showDescription: true,
      showPrice: true,
      showDuration: true,
      showCapabilities: true,
      showQuickCart: true,
      borderColor: "",
      shadowColor: "",
      typeColor: "",
      titleColor: "",
      categoryColor: "",
      subcategoryColor: "",
      descriptionColor: "",
      priceColor: "",
      buttonTextColor: "",
      buttonBackgroundColor: "",
      buttonHoverTextColor: "",
      buttonHoverBackgroundColor: "",
      ctaLabel: "Ver detalle",
      serviceCtaLabel: "Ver y reservar",
      ctaBehavior: "detail",
      ctaUrl: "",
    },
    brands: {
      eyebrow: "",
      title: "Marcas",
      subtitle: "Elegí una marca y explorá el catálogo disponible.",
      loadingText: "Cargando marcas…",
      emptyText: "Todavía no hay marcas para mostrar.",
      errorText: "No se pudieron cargar las marcas.",
      ctaLabel: "Ver productos",
      items: [],
    },
    spotlight: {
      enabled: false,
      eyebrow: "",
      title: "",
      description: "",
      logoUrl: "",
      logoAlt: "",
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      ctaLabel: "",
      ctaUrl: "",
      ctaRequiresProducts: false,
      primaryColor: "",
      secondaryColor: "",
    },
    featured: {
      eyebrow: "",
      title: "Productos destacados",
      subtitle: "Conocé las últimas novedades del catálogo.",
      loadingText: "Cargando productos…",
      emptyText: "Todavía no hay productos para mostrar.",
      errorText: "No se pudieron cargar los productos.",
      ctaLabel: "Ver detalle",
      backgroundColor: "#FFFFFF",
      eyebrowColor: "#F35A3A",
      titleColor: "#19242F",
      subtitleColor: "#5B5D73",
      loadingTextColor: "#5B5D73",
      emptyTextColor: "#1A1A2E",
      errorTextColor: "#D0333A",
    },
    categories: {
      eyebrow: "",
      title: "Explorá por categorías",
      subtitle: "",
      loadingText: "Cargando categorías…",
      emptyText: "Todavía no hay categorías para mostrar.",
      errorText: "No se pudieron cargar las categorías.",
      ctaLabel: "Ver productos",
      productSingular: "producto",
      productPlural: "productos",
      backgroundColor: "#EEF0F5",
      backgroundImageUrl: "",
      backgroundImagePosition: "center",
      eyebrowColor: "#F35A3A",
      titleColor: "#19242F",
      subtitleColor: "#5B5D73",
      loadingTextColor: "#5B5D73",
      loadingSpinnerColor: "#E44E31",
      emptyBackgroundColor: "#E5F6FD",
      emptyTextColor: "#014361",
      emptyBorderColor: "transparent",
      emptyIconColor: "#0288D1",
      errorBackgroundColor: "#FDEDED",
      errorTextColor: "#5F2120",
      errorBorderColor: "transparent",
      errorIconColor: "#D32F2F",
      cardBackgroundColor: "#F7F8FC",
      cardBorderColor: "rgba(26,26,46,.09)",
      cardBorderRadius: 14,
      cardShadowColor: "#272A40",
      cardShadowOpacity: 0.12,
      cardShadowX: 6,
      cardShadowY: 6,
      cardShadowBlur: 16,
      cardShadowSpread: 0,
      imageFit: "cover",
      imagePosition: "center",
      fallbackStartColor: "#494A98",
      fallbackEndColor: "#E44E31",
      fallbackAngle: 135,
      overlayTopColor: "rgba(0,0,0,.14)",
      overlayMiddleColor: "rgba(0,0,0,.12)",
      overlayBottomColor: "rgba(0,0,0,.76)",
      cardTitleColor: "#FFFFFF",
      cardTitleShadowColor: "#000000",
      cardTitleShadowOpacity: 0.55,
      cardTitleShadowX: 0,
      cardTitleShadowY: 3,
      cardTitleShadowBlur: 14,
      countBackgroundColor: "#F35A3A",
      countTextColor: "#FFFFFF",
      countBorderColor: "transparent",
      ctaBackgroundColor: "#F35A3A",
      ctaTextColor: "#FFFFFF",
      ctaHoverBackgroundColor: "#D94D32",
      ctaHoverTextColor: "#FFFFFF",
      ctaBorderColor: "transparent",
      ctaBorderRadius: 999,
    },
  },
  about: {
    eyebrow: "Sobre nuestro negocio",
    title: "Conocé nuestro negocio",
    description:
      "Trabajamos para ofrecer una atención cercana, soluciones claras y una experiencia confiable en cada consulta.",
    imageUrl: "",
    imageAlt: "Conocé nuestro negocio",
    layout: "image-left",
    imagePosition: "center",
    ctaLabel: "Contactanos",
    ctaUrl: "#contact",
    ctaRequiresProducts: false,
    backgroundColor: "#EEF0F5",
    contentBackgroundColor: "transparent",
    contentBorderRadius: 40,
    eyebrowColor: "#F35A3A",
    titleColor: "#19242F",
    descriptionColor: "#5B5D73",
    descriptionToggleColor: "#F35A3A",
    imageStyle: {
      backgroundColor: "rgba(25,36,47,.06)", borderRadius: 50,
      shadowColor: "#000000", shadowOpacity: 0.15, shadowX: 0, shadowY: 24, shadowBlur: 70, shadowSpread: 0,
    },
    ctaStyle: {
      backgroundColor: "#F35A3A", textColor: "#FFFFFF", hoverBackgroundColor: "#E05335", hoverTextColor: "#EBEBEB",
      borderColor: "transparent", borderWidth: 0, borderRadius: 999,
      shadowColor: "#141628", shadowOpacity: 0.14, shadowX: 0, shadowY: 8, shadowBlur: 20, shadowSpread: 0,
      hoverShadowColor: "#141628", hoverShadowOpacity: 0.2, hoverShadowX: 0, hoverShadowY: 12, hoverShadowBlur: 28, hoverShadowSpread: 0,
      focusRingColor: "#F35A3A18",
    },
  },
  highlightsIntro: {
    eyebrow: "Por qué elegirnos",
    title: "Una experiencia pensada para vos",
    subtitle:
      "Combinamos atención, calidad y agilidad para que cada contacto con el negocio sea simple y confiable.",
    backgroundColor: "#FFFFFF",
    backgroundImageUrl: "",
    backgroundImagePosition: "center",
    backgroundImageOpacity: 0.24,
    eyebrowColor: "#F35A3A",
    titleColor: "#19242F",
    subtitleColor: "#5B5D73",
  },
  highlights: [
    {
      id: "atencion-personalizada",
      icon: "",
      title: "Atención personalizada",
      description: "Escuchamos cada necesidad y acompañamos al cliente de forma cercana.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
      cardStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.cardStyle },
      iconStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconStyle },
      iconSurfaceStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle },
      titleStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.titleStyle },
      descriptionStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle },
      imageStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.imageStyle },
      videoStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.videoStyle },
    },
    {
      id: "calidad-y-confianza",
      icon: "",
      title: "Calidad y confianza",
      description: "Cuidamos cada detalle para brindar una experiencia consistente y segura.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
      cardStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.cardStyle },
      iconStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconStyle },
      iconSurfaceStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle },
      titleStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.titleStyle },
      descriptionStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle },
      imageStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.imageStyle },
      videoStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.videoStyle },
    },
    {
      id: "respuesta-agil",
      icon: "",
      title: "Respuesta ágil",
      description: "Facilitamos la consulta y damos respuestas claras para avanzar sin vueltas.",
      imageUrl: "",
      imageAlt: "",
      imagePosition: "center",
      youtubeUrl: "",
      cardStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.cardStyle },
      iconStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconStyle },
      iconSurfaceStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.iconSurfaceStyle },
      titleStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.titleStyle },
      descriptionStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.descriptionStyle },
      imageStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.imageStyle },
      videoStyle: { ...HIGHLIGHT_VISUAL_DEFAULTS.videoStyle },
    },
  ],
  imageCarousel: {
    eyebrow: "Galería",
    title: "Imágenes que hablan por nosotros",
    subtitle: "Mostrá productos, trabajos, espacios o momentos del negocio.",
    items: [], autoplay: true, intervalMs: 4500, animation: "slide", showArrows: true, showDots: true,
    ...IMAGE_CAROUSEL_VISUAL_DEFAULTS,
  },
  testimonials: {
    eyebrow: "Testimonios",
    title: "Lo que dicen nuestros clientes",
    subtitle: "Experiencias reales de personas que ya eligieron el negocio.",
    items: [],
    ...TESTIMONIALS_VISUAL_DEFAULTS,
  },
  contact: {
    title: "Contacto",
    subtitle: "",
    whatsappMessage: "Hola, quiero hacer una consulta.",
    backgroundImageUrl: "",
    backgroundImagePosition: "center",
    detailsCtaLabel: "Ver contacto",
    detailsCtaUrl: "/contact",
    whatsappCtaLabel: "Escribir por WhatsApp",
    floatingWhatsappAriaLabel: "Contactar por WhatsApp",
    mapTitle: "Ubicación",
    backgroundColor: "#19242F",
    imageBackgroundColor: "rgba(255,255,255,.08)",
    decorativeAccentColor: "#F35A3A",
    titleColor: "#FFFFFF",
    subtitleColor: "rgba(255,255,255,.82)",
    addressTextColor: "rgba(255,255,255,.82)",
    scheduleTextColor: "rgba(255,255,255,.82)",
    phoneTextColor: "rgba(255,255,255,.82)",
    emailTextColor: "rgba(255,255,255,.82)",
    addressIconColor: "#F35A3A",
    scheduleIconColor: "#F35A3A",
    phoneIconColor: "#F35A3A",
    emailIconColor: "#F35A3A",
    detailsCtaStyle: { textColor: "#FFFFFF" },
  },
  footer: {
    style: "style-1",
    showLogo: true,
    logoPosition: "center",
    tagline: "",
    copyright: "{businessName}. Todos los derechos reservados.",
    navigationTitle: "Navegación",
    contactTitle: "Contacto",
    homeLabel: "Inicio",
    productsLabel: "Productos",
    contactLabel: "Contacto",
  },
  messages: {
    maintenanceTitle: "Estamos actualizando el sitio",
    maintenanceDescription:
      "{businessName} se encuentra temporalmente en mantenimiento.",
    unavailableTitle: "Sitio no disponible",
    unavailableDescription:
      "La presencia pública de este proyecto todavía no está habilitada.",
    catalogDisabledTitle: "Catálogo no disponible",
    catalogDisabledDescription: "El catálogo no está habilitado para este sitio.",
    ordersDisabledTitle: "Pedidos no disponibles",
    ordersDisabledDescription:
      "Podés consultar el catálogo y comunicarte directamente con el comercio.",
    ecommerceDisabledTitle: "Pago online no disponible",
    ecommerceDisabledDescription:
      "El pedido puede coordinarse directamente con el comercio.",
  },
  pages: {
    contact: { style: CONTACT_PAGE_STYLE_DEFAULTS },
  },
  notifications: {
    webPushPrompt: {
      enabled: true,
      title: "Recibí novedades",
      subscribedTitle: "Notificaciones activadas",
      description: "Activá los avisos de {businessName} para enterarte de novedades y promociones.",
      subscribedDescription: "Este navegador recibe avisos de {businessName}.",
      enableLabel: "Activar notificaciones",
      disableLabel: "Desactivar",
      dismissLabel: "Ahora no",
      processingLabel: "Procesando…",
      successMessage: "Notificaciones activadas correctamente.",
      disableSuccessMessage: "Notificaciones desactivadas.",
      errorMessage: "No se pudieron activar las notificaciones.",
      disableErrorMessage: "No se pudieron desactivar las notificaciones.",
      position: "bottom-right",
      style: WEB_PUSH_PROMPT_STYLE_DEFAULTS,
    },
  },
  theme: {
    template: "catalog-basic",
    primaryColor: "#F35A3A",
    secondaryColor: "#19242F",
    surfaceColor: "#F7F8FC",
    backgroundColor: "#EEF0F5",
    textColor: "#1A1A2E",
    mutedTextColor: "#5B5D73",
    dividerColor: "rgba(26,26,46,.09)",
    logoUrl: "",
    compactLogoUrl: "",
    faviconUrl: "",
    fontFamily: "Asap",
    headingFontFamily: "Exo 2",
    bodyFontFamily: "Asap",
    googleFontsUrl: "",
    fonts: [],
    borderRadius: 10,
    mode: "light",
  },
  seo: {
    title: "",
    description: "",
    imageUrl: "",
  },
  widgets: {
    floatingWhatsapp: {
      style: {
        ...FLOATING_WHATSAPP_STYLE_DEFAULTS,
        hover: { ...FLOATING_WHATSAPP_STYLE_DEFAULTS.hover },
      },
    },
  },
  sections: {
    showNavbar: true,
    showHero: true,
    showSpotlight: true,
    showBrands: true,
    showProducts: true,
    showFeatured: true,
    showCategories: true,
    showAbout: true,
    showHighlights: true,
    showImageCarousel: false,
    showTestimonials: false,
    showContact: true,
    showFooter: true,
    showFloatingWhatsapp: true,
  },
  layout: {
    segments: [
      { id: "navbar", type: "navbar" },
      { id: "hero", type: "hero" },
      { id: "spotlight", type: "spotlight" },
      { id: "featured", type: "featured" },
      { id: "brands", type: "brands" },
      { id: "categories", type: "categories" },
      { id: "about", type: "about" },
      { id: "highlights", type: "highlights" },
      { id: "contact", type: "contact" },
      { id: "footer", type: "footer" },
    ],
  },
  updatedAt: null,
});

function normalizeHighlightItems(source = []) {
  const seen = new Set();
  return (Array.isArray(source) ? source : []).map((item, index) => {
    const raw = String(item?.id || item?.title || `diferencial-${index + 1}`);
    const base =
      raw
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || `diferencial-${index + 1}`;
    let id = base;
    let suffix = 2;
    while (seen.has(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }
    seen.add(id);
    return {
      ...item,
      id,
      icon: String(item?.icon || "").trim(),
      imageAlt: item?.imageAlt || "",
      imagePosition: normalizeImagePosition(item?.imagePosition),
      youtubeUrl: String(item?.youtubeUrl || "").trim(),
    };
  });
}

function deepMerge(base, source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return Array.isArray(base) ? [...base] : { ...base };
  }
  const output = { ...base };
  for (const [key, value] of Object.entries(source)) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base?.[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      output[key] = deepMerge(base[key], value);
    } else if (value !== undefined) {
      output[key] = value;
    }
  }
  return output;
}

/**
 * Compatibilidad estructural para publicaciones históricas.
 *
 * Algunos documentos legacy pueden conservar `null`, objetos donde hoy se
 * espera un array o arrays donde hoy se espera un objeto. `deepMerge` debe
 * preservar esos valores durante la adaptación, pero el renderer moderno
 * necesita que los nodos estructurales tengan como mínimo la forma del
 * contrato actual. Sólo corregimos incompatibilidades de TIPO siguiendo el
 * shape de DEFAULT_LANDING; valores modernos válidos (incluidos strings
 * vacíos, false, 0 y objetos custom) quedan intactos.
 */
function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stabilizeLandingShape(value, fallback) {
  if (Array.isArray(fallback)) {
    return Array.isArray(value) ? value : [...fallback];
  }

  if (!isPlainObject(fallback)) return value;

  const output = isPlainObject(value) ? { ...value } : {};
  Object.entries(fallback).forEach(([key, fallbackValue]) => {
    if (Array.isArray(fallbackValue)) {
      if (!Array.isArray(output[key])) output[key] = [...fallbackValue];
      return;
    }

    if (isPlainObject(fallbackValue)) {
      output[key] = stabilizeLandingShape(output[key], fallbackValue);
    }
  });

  return output;
}


function hasLegacyVisualValue(value) {
  return value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");
}
function legacyVisualValue(...values) { return values.find(hasLegacyVisualValue); }
function assignLegacyVisual(target, key, value) {
  if (!target || hasLegacyVisualValue(target[key]) || value === undefined) return;
  target[key] = value;
}
function materializeLegacyAppearance(input = {}, inheritedTheme = {}) {
  const source = deepMerge({}, input || {});
  const appearance = source.appearance && typeof source.appearance === "object" ? source.appearance : {};
  const theme = source.theme && typeof source.theme === "object" ? source.theme : inheritedTheme;

  const contactLegacy = appearance.contact && typeof appearance.contact === "object" ? appearance.contact : {};
  if (Object.prototype.hasOwnProperty.call(appearance, "contact")) {
    source.contact = source.contact && typeof source.contact === "object" ? source.contact : {};
    const accent = legacyVisualValue(contactLegacy.accentColor, theme?.primaryColor, "#F35A3A");
    const body = legacyVisualValue(contactLegacy.textColor, "rgba(255,255,255,.82)");
    assignLegacyVisual(source.contact, "backgroundColor", legacyVisualValue(contactLegacy.backgroundColor, theme?.secondaryColor, "#19242F"));
    assignLegacyVisual(source.contact, "imageBackgroundColor", legacyVisualValue(contactLegacy.surfaceColor, "rgba(255,255,255,.08)"));
    assignLegacyVisual(source.contact, "decorativeAccentColor", accent);
    assignLegacyVisual(source.contact, "titleColor", legacyVisualValue(contactLegacy.titleColor, "#FFFFFF"));
    assignLegacyVisual(source.contact, "subtitleColor", body);
    ["addressTextColor", "scheduleTextColor", "phoneTextColor", "emailTextColor"].forEach((key) => assignLegacyVisual(source.contact, key, body));
    ["addressIconColor", "scheduleIconColor", "phoneIconColor", "emailIconColor"].forEach((key) => assignLegacyVisual(source.contact, key, accent));
    source.contact.detailsCtaStyle = source.contact.detailsCtaStyle && typeof source.contact.detailsCtaStyle === "object" ? source.contact.detailsCtaStyle : {};
    assignLegacyVisual(source.contact.detailsCtaStyle, "textColor", legacyVisualValue(contactLegacy.titleColor, "#FFFFFF"));
  }
  if (Array.isArray(source.layout?.segments)) {
    source.layout = { ...source.layout, segments: source.layout.segments.map((segment) => {
      if (!segment || segment.type !== "contact" || !segment.data) return segment;
      return { ...segment, data: materializeLegacyAppearance(segment.data, theme) };
    }) };
  }
  delete source.appearance;
  return source;
}

export function normalizeLanding(payload = {}, fallback = DEFAULT_LANDING) {
  const adapted = adaptPublicLandingResponse(payload);
  const rawSource = adapted.landing || {};
  const contactPageSource = materializeContactPageVisualOwnership(rawSource);
  const webPushSource = materializeWebPushPromptVisualOwnership(contactPageSource);
  const appearanceSource = materializeLegacyAppearance(webPushSource, webPushSource?.theme);
  const ownershipSource = materializeHighlightsOwnership(materializeCarouselTestimonialsOwnership(appearanceSource));
  const source = {
    ...ownershipSource,
    navigation: materializeMegaMenuVisual(ownershipSource?.navigation, ownershipSource?.theme, ownershipSource?.designTokens),
  };
  if (Array.isArray(source.layout?.segments)) {
    source.layout = {
      ...source.layout,
      segments: source.layout.segments.map((segment) => {
        if (!(segment?.id === "navbar" || segment?.type === "navbar") || !segment?.data) return segment;
        const data = { ...segment.data };
        return {
          ...segment,
          data: {
            ...data,
            navigation: materializeMegaMenuVisual(data.navigation, data.theme || source.theme, data.designTokens || source.designTokens),
          },
        };
      }),
    };
  }
  source.catalog = {
    ...(source.catalog || {}),
    productsPage: materializeProductsPageVisualFromLegacy(source.catalog?.productsPage, source.theme),
  };
  const publication = adapted.publication || {};
  // Un payload legacy mal tipado nunca debe poder derribar el storefront.
  // Reponemos únicamente la FORMA estructural requerida por el contrato; el
  // contenido válido del cliente sigue siendo la fuente de verdad.
  const merged = stabilizeLandingShape(deepMerge(fallback, source), fallback);

  const publishedRevision = Number(
    publication.publishedRevision ?? source.publishedRevision ?? 0
  );
  merged.schemaVersion = 26;
  merged.publicStatus = String(
    publication.status || source.publicStatus || fallback.publicStatus || "draft"
  );
  merged.publishedRevision = Number.isSafeInteger(publishedRevision)
    ? publishedRevision
    : 0;
  merged.revision = Number(
    publication.revision ||
      merged.publishedRevision ||
      source.revision ||
      fallback.revision ||
      1
  );
  merged.hero.slides = Array.isArray(source.hero?.slides)
    ? source.hero.slides.filter((slide) => slide?.url)
    : Array.isArray(merged.hero.slides)
      ? merged.hero.slides.filter((slide) => slide?.url)
      : [];
  merged.hero.rotatingPhrases = Array.isArray(source.hero?.rotatingPhrases)
    ? source.hero.rotatingPhrases.filter(Boolean)
    : Array.isArray(merged.hero.rotatingPhrases)
      ? merged.hero.rotatingPhrases.filter(Boolean)
      : [];
  merged.catalog.brands.items = Array.isArray(source.catalog?.brands?.items)
    ? source.catalog.brands.items.filter((item) => (item?.name ?? item?.label) || item?.logoUrl)
    : Array.isArray(merged.catalog?.brands?.items)
      ? merged.catalog.brands.items.filter((item) => (item?.name ?? item?.label) || item?.logoUrl)
      : [];
  merged.theme.fonts = Array.isArray(source.theme?.fonts)
    ? source.theme.fonts.filter((font) => font?.family && font?.url)
    : Array.isArray(merged.theme?.fonts)
      ? merged.theme.fonts.filter((font) => font?.family && font?.url)
      : [];
  const highlightSource = Array.isArray(source.highlights)
    ? source.highlights
    : merged.highlights;
  merged.highlights = materializeHighlightItemsVisual(normalizeHighlightItems(
    Array.isArray(highlightSource)
      ? highlightSource.filter(
          (item) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl
        )
      : []
  ));

  // deepMerge ya diferencia una propiedad ausente de un string vacío. No se
  // deben reponer textos iniciales cuando la publicación/preview trae "".
  merged.hero.imagePosition = normalizeImagePosition(merged.hero?.imagePosition);
  merged.hero.slides = Array.isArray(merged.hero?.slides)
    ? merged.hero.slides.map((slide) => ({
        ...slide,
        position: normalizeImagePosition(slide?.position, merged.hero.imagePosition),
      }))
    : [];
  merged.about.layout = ["image-left", "image-right"].includes(merged.about?.layout)
    ? merged.about.layout
    : "image-left";
  merged.about.imagePosition = normalizeImagePosition(merged.about?.imagePosition);
  merged.catalog.spotlight.backgroundImagePosition = normalizeImagePosition(
    merged.catalog?.spotlight?.backgroundImagePosition
  );
  merged.catalog.categories.backgroundImagePosition = normalizeImagePosition(
    merged.catalog?.categories?.backgroundImagePosition
  );
  merged.catalog.categories.imagePosition = normalizeImagePosition(
    merged.catalog?.categories?.imagePosition
  );
  merged.imageCarousel.items = Array.isArray(source.imageCarousel?.items)
    ? source.imageCarousel.items.filter((item) => item?.imageUrl || item?.caption).map((item) => ({ ...item, imagePosition: normalizeImagePosition(item?.imagePosition) }))
    : Array.isArray(merged.imageCarousel?.items)
      ? merged.imageCarousel.items.filter((item) => item?.imageUrl || item?.caption).map((item) => ({ ...item, imagePosition: normalizeImagePosition(item?.imagePosition) }))
      : [];
  merged.testimonials.items = Array.isArray(source.testimonials?.items)
    ? source.testimonials.items.filter((item) => item?.quote || item?.name || item?.role || item?.avatarUrl)
    : Array.isArray(merged.testimonials?.items)
      ? merged.testimonials.items.filter((item) => item?.quote || item?.name || item?.role || item?.avatarUrl)
      : [];
  merged.contact.backgroundImagePosition = normalizeImagePosition(
    merged.contact?.backgroundImagePosition
  );
  merged.highlightsIntro.backgroundImagePosition = normalizeImagePosition(
    merged.highlightsIntro?.backgroundImagePosition
  );
  const highlightBackgroundOpacity = Number(merged.highlightsIntro?.backgroundImageOpacity);
  merged.highlightsIntro.backgroundImageOpacity = Number.isFinite(highlightBackgroundOpacity)
    ? Math.max(0, Math.min(1, highlightBackgroundOpacity))
    : 0.24;
  merged.footer.logoPosition = normalizeImagePosition(merged.footer?.logoPosition);

  merged.schemaVersion = 26;
  return merged;
}

export function LandingProvider({ children }) {
  const { project, theme: projectTheme, productsEnabled, gastronomicMenuEnabled } = useProjectConfig();
  const projectFallback = useMemo(() => {
    const projectName = String(project?.name || DEFAULT_LANDING.business.name).trim();
    return resolveSiteExperienceDefaults(deepMerge(DEFAULT_LANDING, {
      business: { name: projectName },
      hero: { title: projectName },
      about: {
        title: `Conocé ${projectName}`,
        imageAlt: `Conocé ${projectName}`,
      },
      seo: { title: projectName },
      theme: projectTheme || {},
    }), { productsEnabled, gastronomicMenuEnabled });
  }, [project?.name, projectTheme, productsEnabled, gastronomicMenuEnabled]);

  const [previewState, setPreviewState] = useState(resolveSitePreviewSession);
  const [previewMeta, setPreviewMeta] = useState(() => ({
    draftRevision: previewState.session?.draftRevision || 0,
    publishedRevision: 0,
    publicStatus: "draft",
    hasUnpublishedChanges: false,
    expiresAt: previewState.session?.expiresAt || null,
  }));
  const [landing, setLanding] = useState(projectFallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(previewState.error || "");
  const [usingFallback, setUsingFallback] = useState(true);
  const [publication, setPublication] = useState(() => ({
    available: false,
    legacy: false,
    publishedRevision: 0,
    status: previewState.requested ? "preview" : "draft",
  }));

  const resetUnavailable = useCallback(
    (message, status = "unavailable") => {
      setLanding(projectFallback);
      setPublication({
        available: false,
        legacy: false,
        publishedRevision: 0,
        status,
      });
      setUsingFallback(true);
      setError(message);
    },
    [projectFallback]
  );

  const refreshLanding = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (previewState.requested && !previewState.session) {
        resetUnavailable(
          previewState.error || "La sesión temporal de vista previa no es válida.",
          "preview-unavailable"
        );
        return null;
      }

      const isPreviewRequest = Boolean(previewState.session);
      const payload = isPreviewRequest
        ? await getSiteDraftPreview(previewState.session)
        : await getPublicLanding();
      // getSiteDraftPreview ya entrega un contrato adaptado de preview. Volver a
      // pasarlo por el adaptador público lo marcaría como no disponible porque
      // su estado es deliberadamente "preview", no "published".
      const adapted = isPreviewRequest
        ? payload
        : adaptPublicLandingResponse(payload);
      const normalized = resolveSiteExperienceDefaults(
        normalizeLanding(adapted, projectFallback),
        { productsEnabled, gastronomicMenuEnabled },
      );
      normalized.theme.logoUrl =
        normalized.theme?.logoUrl || projectTheme?.logoUrl || "";
      normalized.theme.compactLogoUrl =
        normalized.theme?.compactLogoUrl ||
        projectTheme?.compactLogoUrl ||
        normalized.theme.logoUrl ||
        "";
      normalized.theme.faviconUrl =
        normalized.theme?.faviconUrl ||
        projectTheme?.faviconUrl ||
        normalized.theme.compactLogoUrl ||
        normalized.theme.logoUrl ||
        "";

      setLanding(normalized);
      setPublication(adapted.publication);
      setUsingFallback(adapted.publication?.available !== true);
      if (isPreviewRequest) {
        setPreviewMeta({
          draftRevision:
            payload?.preview?.draftRevision ||
            previewState.session?.draftRevision ||
            normalized.revision ||
            0,
          publishedRevision: payload?.preview?.publishedRevision || 0,
          publicStatus: payload?.preview?.publicStatus || "draft",
          hasUnpublishedChanges: payload?.preview?.hasUnpublishedChanges === true,
          expiresAt:
            payload?.preview?.expiresAt ||
            previewState.session?.expiresAt ||
            null,
        });
      }
      return normalized;
    } catch (requestError) {
      const isPreviewRequest =
        previewState.requested || Boolean(previewState.session);
      const message =
        requestError?.response?.data?.message ||
        requestError?.message ||
        (isPreviewRequest
          ? "No se pudo validar la vista previa temporal."
          : "No se pudo cargar el contenido público del sitio.");

      if (isPreviewRequest && shouldDiscardSitePreviewSession(requestError)) {
        clearSitePreviewSession();
        setPreviewState({ requested: true, session: null, error: message });
      }

      resetUnavailable(
        message,
        isPreviewRequest ? "preview-unavailable" : "unavailable"
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [previewState, projectFallback, projectTheme, productsEnabled, gastronomicMenuEnabled, resetUnavailable]);

  const exitPreview = useCallback(() => {
    clearSitePreviewSession();
    setPreviewState({ requested: false, session: null, error: "" });
    setPreviewMeta({
      draftRevision: 0,
      publishedRevision: 0,
      publicStatus: "draft",
      hasUnpublishedChanges: false,
      expiresAt: null,
    });

    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    refreshLanding();
  }, [refreshLanding]);

  useEffect(() => {
    const expiresAt = Date.parse(
      previewMeta.expiresAt || previewState.session?.expiresAt || ""
    );
    if (!previewState.session || !Number.isFinite(expiresAt)) return undefined;

    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      clearSitePreviewSession();
      setPreviewState({
        requested: true,
        session: null,
        error: "La vista previa temporal venció.",
      });
      resetUnavailable("La vista previa temporal venció.", "preview-unavailable");
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      clearSitePreviewSession();
      setPreviewState({
        requested: true,
        session: null,
        error: "La vista previa temporal venció.",
      });
      resetUnavailable("La vista previa temporal venció.", "preview-unavailable");
    }, Math.min(remaining + 250, 2147483647));

    return () => window.clearTimeout(timeoutId);
  }, [previewMeta.expiresAt, previewState.session, resetUnavailable]);

  useEffect(() => {
    if (!previewState.session || typeof document === "undefined") return undefined;

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const created = !robotsMeta;
    const previousContent = robotsMeta?.getAttribute("content");
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");

    return () => {
      if (created) {
        robotsMeta?.remove();
      } else if (previousContent === null) {
        robotsMeta?.removeAttribute("content");
      } else {
        robotsMeta?.setAttribute("content", previousContent);
      }
    };
  }, [previewState.session]);

  useEffect(() => {
    // Mientras la publicación real todavía no llegó, conservar el <head> que
    // entregó el servidor. Así React no reemplaza SEO/favicon válidos por los
    // defaults "Mi negocio" / WebProfit durante los primeros milisegundos.
    if (usingFallback && !previewState.session) return;
    syncDocumentBranding(landing);
  }, [landing, previewState.session, usingFallback]);

  const value = useMemo(
    () => ({
      ...landing,
      landing,
      loading,
      error,
      usingFallback,
      publicationAvailable: publication.available === true,
      legacyPublication: publication.legacy === true,
      publishedRevision:
        publication.publishedRevision || landing.publishedRevision || 0,
      maintenance: landing.publicStatus === "maintenance",
      previewActive: Boolean(previewState.session),
      previewRequested: previewState.requested === true,
      previewRevision:
        previewMeta.draftRevision ||
        previewState.session?.draftRevision ||
        (landing.publicStatus === "preview" ? landing.revision : 0) ||
        0,
      previewPublishedRevision: previewMeta.publishedRevision || 0,
      previewPublicStatus: previewMeta.publicStatus || "draft",
      previewHasUnpublishedChanges: previewMeta.hasUnpublishedChanges === true,
      previewExpiresAt:
        previewMeta.expiresAt || previewState.session?.expiresAt || null,
      refreshLanding,
      exitPreview,
    }),
    [
      landing,
      loading,
      error,
      usingFallback,
      publication,
      previewState,
      previewMeta,
      refreshLanding,
      exitPreview,
    ]
  );

  return <LandingContext.Provider value={value}>{children}</LandingContext.Provider>;
}

export function LandingScopeProvider({ landing: scopedLanding, children }) {
  const parent = useContext(LandingContext);
  if (!parent) {
    throw new Error("LandingScopeProvider debe usarse dentro de <LandingProvider />");
  }
  const value = useMemo(() => ({
    ...parent,
    ...scopedLanding,
    landing: scopedLanding,
  }), [parent, scopedLanding]);
  return <LandingContext.Provider value={value}>{children}</LandingContext.Provider>;
}

export function useLanding() {
  const context = useContext(LandingContext);
  if (!context) {
    throw new Error("useLanding debe usarse dentro de <LandingProvider />");
  }
  return context;
}
